import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import * as Icons from "lucide-react";
import { NAV_ITEMS } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { useLayout } from "@/context/LayoutContext";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

// Per-department visual identity — icon tiles stay colored, cards unified to dark bg.
const DEPT_THEME = {
  ops_center:     { card: "bg-white/5",  tile: "bg-[#1A3A5C]", tileIcon: "text-[#2F80ED]",  label: "text-[#AEB9CC]" },
  live_ops:       { card: "bg-white/5",  tile: "bg-[#2F80ED]", tileIcon: "text-white",      label: "text-[#AEB9CC]" },
  dashboard:      { card: "bg-white/5",  tile: "bg-[#243447]", tileIcon: "text-brand-gold", label: "text-[#AEB9CC]" },
  communications: { card: "bg-white/5",  tile: "bg-amber-700",  tileIcon: "text-white",      label: "text-[#AEB9CC]" },
  crm:            { card: "bg-white/5",  tile: "bg-[#3CA831]",  tileIcon: "text-white",      label: "text-[#AEB9CC]" },
  organizers:     { card: "bg-white/5",  tile: "bg-yellow-600", tileIcon: "text-white",      label: "text-[#AEB9CC]" },
  documents:      { card: "bg-white/5",  tile: "bg-teal-700",   tileIcon: "text-white",      label: "text-[#AEB9CC]" },
  scheduling:     { card: "bg-white/5",  tile: "bg-[#374151]",  tileIcon: "text-white",      label: "text-[#AEB9CC]" },
  executive:      { card: "bg-white/5",  tile: "bg-[#374151]",  tileIcon: "text-brand-gold", label: "text-[#AEB9CC]" },
  administration: { card: "bg-white/5",  tile: "bg-zinc-600",   tileIcon: "text-white",      label: "text-[#AEB9CC]" },
  ai_center:      { card: "bg-white/5",  tile: "bg-sky-600",    tileIcon: "text-white",      label: "text-[#AEB9CC]" },
};
const DEFAULT_THEME = { card: "bg-white/5", tile: "bg-[#243447]", tileIcon: "text-brand-gold", label: "text-[#AEB9CC]" };
const themeFor = (key) => DEPT_THEME[key] || DEFAULT_THEME;

function NavIcon({ name, className }) {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} strokeWidth={2} />;
}

function Badge({ value, testid }) {
  if (!value) return null;
  return (
    <span data-testid={testid}
      className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center shrink-0">
      {value > 9 ? "9+" : value}
    </span>
  );
}

// A single child (leaf) row inside an expanded department card.
function ChildRow({ item, badge, onNavigate }) {
  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      onClick={onNavigate}
      data-testid={`nav-${item.key}`}
      className={({ isActive }) =>
        cn(
          "group/row flex items-center gap-2.5 rounded-lg pl-3 pr-2 py-2 text-sm transition-colors relative",
          isActive
            ? "bg-[#2F80ED] text-white font-semibold shadow-sm"
            : "text-[#AEB9CC] hover:bg-[#283548] hover:text-white font-medium"
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-white/60" />}
          <NavIcon name={item.icon} className={cn("h-[17px] w-[17px] shrink-0", isActive ? "text-white" : "text-[#AEB9CC]/70 group-hover/row:text-white")} />
          <span className="truncate flex-1">{item.label}</span>
          <Badge value={badge} testid={`nav-badge-${item.key}`} />
        </>
      )}
    </NavLink>
  );
}

// Expanded department "card": header tile + label + collapsible children.
function DeptCard({ item, children, badges, onNavigate }) {
  const t = themeFor(item.key);
  const storageKey = `tf-nav-group-${item.key}`;
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved === null ? true : saved === "true";
  });
  const toggle = () => setOpen((o) => { localStorage.setItem(storageKey, String(!o)); return !o; });
  const headerBadge = badges?.[item.key];

  return (
    <div className={cn("rounded-xl p-2 transition-shadow", t.card)} data-testid={`nav-group-${item.key}`}>
      <div className="flex items-center gap-2">
        <NavLink
          to={item.path}
          onClick={onNavigate}
          data-testid={`nav-${item.key}`}
          className="flex items-center gap-2.5 flex-1 min-w-0 rounded-lg px-1 py-1 hover:bg-[#283548] transition-colors"
        >
          <span className={cn("h-9 w-9 rounded-lg flex items-center justify-center shadow-sm shrink-0", t.tile)}>
            <NavIcon name={item.icon} className={cn("h-[18px] w-[18px]", t.tileIcon)} />
          </span>
          <span className={cn("truncate text-[13px] font-bold uppercase tracking-wide", t.label)}>{item.label}</span>
        </NavLink>
        <Badge value={headerBadge} testid={`nav-badge-${item.key}`} />
        <button onClick={toggle} data-testid={`nav-group-toggle-${item.key}`} aria-label={`Toggle ${item.label} submenu`}
          className="shrink-0 p-1.5 rounded-md text-[#AEB9CC] hover:text-white hover:bg-[#283548] transition-colors">
          <Icons.ChevronDown className={cn("h-4 w-4 transition-transform duration-150", open ? "" : "-rotate-90")} />
        </button>
      </div>
      {open && children.length > 0 && (
        <div className="mt-1 space-y-0.5" data-testid={`nav-group-children-${item.key}`}>
          {children.map((c) => <ChildRow key={c.key} item={c} badge={badges?.[c.key]} onNavigate={onNavigate} />)}
        </div>
      )}
    </div>
  );
}

// Collapsed rail: colored icon tile + hover fly-out listing the department + children.
function DeptRail({ item, children, badges, onNavigate }) {
  const t = themeFor(item.key);
  const headerBadge = badges?.[item.key];
  return (
    <div className="relative group flex justify-center" data-testid={`nav-rail-${item.key}`}>
      <NavLink
        to={item.path}
        end={item.path === "/"}
        onClick={onNavigate}
        data-testid={`nav-${item.key}`}
        className={({ isActive }) =>
          cn("relative h-11 w-11 rounded-xl flex items-center justify-center shadow-sm transition-transform hover:scale-105", t.tile,
            isActive && "ring-2 ring-[#2F80ED] ring-offset-2 ring-offset-[#1C2431]")
        }
      >
        <NavIcon name={item.icon} className={cn("h-[19px] w-[19px]", t.tileIcon)} />
        {headerBadge ? (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-semibold flex items-center justify-center">
            {headerBadge > 9 ? "9+" : headerBadge}
          </span>
        ) : null}
      </NavLink>

      {/* Rich fly-out on hover */}
      <div className="pointer-events-none group-hover:pointer-events-auto absolute left-full top-0 ml-2 z-50 hidden group-hover:block">
        <div className="w-56 bg-[#1C2431] rounded-xl shadow-xl border border-[#283548] p-2 animate-in fade-in slide-in-from-left-1 duration-150">
          <div className={cn("px-2 py-1.5 mb-1 rounded-lg text-[12px] font-bold uppercase tracking-wide bg-[#283548] text-[#AEB9CC]")}>{item.label}</div>
          <div className="space-y-0.5">
            {children.length === 0 ? (
              <NavLink to={item.path} onClick={onNavigate}
                className={({ isActive }) => cn("flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm", isActive ? "bg-[#2F80ED] text-white font-semibold" : "text-[#AEB9CC] hover:bg-[#283548]")}>
                <NavIcon name={item.icon} className="h-4 w-4 text-[#AEB9CC]/70" /> Open
              </NavLink>
            ) : children.map((c) => (
              <NavLink key={c.key} to={c.path} onClick={onNavigate} data-testid={`nav-flyout-${c.key}`}
                className={({ isActive }) => cn("flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm", isActive ? "bg-[#2F80ED] text-white font-semibold" : "text-[#AEB9CC] hover:bg-[#283548]")}>
                <NavIcon name={c.icon} className="h-4 w-4 text-[#AEB9CC]/70 shrink-0" />
                <span className="truncate flex-1">{c.label}</span>
                <Badge value={badges?.[c.key]} />
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Operations Center — unified department card with per-workflow color accents ───
const OPS_CHILD_META = {
  live_ops:       { color: "#2F80ED", activeBg: "bg-[#2F80ED]",   iconClass: "text-[#2F80ED]" },
  sales:          { color: "#D97706", activeBg: "bg-amber-600",    iconClass: "text-amber-400" },
  client_success: { color: "#0D9488", activeBg: "bg-teal-700",     iconClass: "text-teal-400" },
  production:     { color: "#7C3AED", activeBg: "bg-violet-700",   iconClass: "text-violet-400" },
};

function OpsCenterDeptCard({ item, children, badges, onNavigate }) {
  const location = useLocation();
  const storageKey = "tf-nav-group-ops_center";
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved === null ? true : saved === "true";
  });
  const toggle = () => setOpen((o) => { localStorage.setItem(storageKey, String(!o)); return !o; });
  const t = themeFor("ops_center");
  const headerBadge = badges?.["live_ops"] || badges?.["ops_center"];

  return (
    <div className={cn("rounded-xl p-2 transition-shadow", t.card)} data-testid="nav-group-ops_center">
      <div className="flex items-center gap-2">
        <NavLink
          to="/"
          end
          onClick={onNavigate}
          data-testid="nav-ops_center"
          className="flex items-center gap-2.5 flex-1 min-w-0 rounded-lg px-1 py-1 hover:bg-[#283548] transition-colors"
        >
          <span className={cn("h-9 w-9 rounded-lg flex items-center justify-center shadow-sm shrink-0", t.tile)}>
            <NavIcon name="BriefcaseBusiness" className={cn("h-[18px] w-[18px]", t.tileIcon)} />
          </span>
          <div className="min-w-0 flex-1">
            <div className={cn("truncate text-[13px] font-bold uppercase tracking-wide", t.label)}>Operations Center</div>
            <div className="text-[10px] text-[#4B5563] truncate">Daily operations and workflows</div>
          </div>
        </NavLink>
        <Badge value={headerBadge} testid="nav-badge-ops_center" />
        <button onClick={toggle} data-testid="nav-group-toggle-ops_center" aria-label="Toggle Operations Center submenu"
          className="shrink-0 p-1.5 rounded-md text-[#AEB9CC] hover:text-white hover:bg-[#283548] transition-colors">
          <Icons.ChevronDown className={cn("h-4 w-4 transition-transform duration-150", open ? "" : "-rotate-90")} />
        </button>
      </div>
      {open && children.length > 0 && (
        <div className="mt-1 space-y-0.5" data-testid="nav-group-children-ops_center">
          {children.map((c) => {
            const meta = OPS_CHILD_META[c.key] || {};
            const Ic = Icons[c.icon] || Icons.Circle;
            const isActive = c.path === "/" ? location.pathname === "/" : location.pathname.startsWith(c.path);
            const badge = badges?.[c.key];
            return (
              <NavLink
                key={c.key}
                to={c.path}
                end={c.path === "/"}
                onClick={onNavigate}
                data-testid={`nav-${c.key}`}
                className={cn(
                  "group/row flex items-center gap-2.5 rounded-lg pl-3 pr-2 py-2 text-sm transition-colors relative",
                  isActive
                    ? cn(meta.activeBg, "text-white font-semibold shadow-sm")
                    : "text-[#AEB9CC] hover:bg-[#283548] hover:text-white font-medium"
                )}
              >
                {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-white/60" />}
                <Ic className={cn("h-[17px] w-[17px] shrink-0", isActive ? "text-white" : meta.iconClass)} />
                <span className="truncate flex-1">{c.label}</span>
                {badge && (
                  <span className={cn("min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-semibold flex items-center justify-center shrink-0",
                    isActive ? "bg-white/20" : "bg-red-500")}>
                    {badge > 9 ? "9+" : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Communications Center — full navigation hierarchy in primary sidebar ─────
const PRIMARY_CH_DEFS = [
  { key: "ch_email",   label: "Email",        icon: "Mail",          tone: "text-blue-400",    subs: [["ch_email_leads","Leads/Prospects","UserPlus"],["ch_email_active","Active Clients","UserCheck"],["ch_email_inactive","Inactive Clients","UserMinus"]] },
  { key: "ch_portal",  label: "Secure Portal", icon: "Globe",         tone: "text-violet-400",  subs: [["ch_portal_leads","Leads/Prospects","UserPlus"],["ch_portal_active","Active Clients","UserCheck"],["ch_portal_inactive","Inactive Clients","UserMinus"]] },
  { key: "ch_webchat", label: "Website Chat",  icon: "MessageSquare", tone: "text-emerald-400", subs: [["ch_webchat_leads","Leads/Prospects","UserPlus"],["ch_webchat_active","Active Clients","UserCheck"],["ch_webchat_inactive","Inactive Clients","UserMinus"]] },
];
const MORE_CH_DEFS = [
  { key: "ch_sms",   label: "SMS",         icon: "Phone",     tone: "text-amber-400" },
  { key: "ch_phone", label: "Phone Calls", icon: "PhoneCall", tone: "text-slate-400"  },
];

function CommsDeptCard({ item, badges, onNavigate }) {
  const { user } = useAuth();
  const isMgmt = ["administrator", "partner", "manager"].includes(user?.role);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(() => localStorage.getItem("tf-nav-group-communications") !== "false");
  const [internalOpen, setInternalOpen] = useState(true);
  const [closedOpen, setClosedOpen] = useState(false);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [moreChannelsOpen, setMoreChannelsOpen] = useState(false);
  const [channelExpanded, setChannelExpanded] = useState({});

  const isCommsRoute = location.pathname === "/communications/center";
  const currentQkey = isCommsRoute ? (searchParams.get("qkey") || "all_comms") : null;

  useEffect(() => {
    const load = () => api.get("/communications/counts").then(r => setCounts(r.data || {})).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const toggle = () => { const v = !open; localStorage.setItem("tf-nav-group-communications", String(v)); setOpen(v); };
  const goTo = (qkey) => { navigate(`/communications/center?qkey=${qkey}`); onNavigate?.(); };

  // UB = unanswered_buckets — used for external-channel and inbox badge counts.
  // Internal message counts stay on the counts object (they represent unread, not unanswered).
  const UB = counts?.unanswered_buckets || {};

  const NavItem = ({ qkey, label, icon, countKey, tone, indent, testid, useUB }) => {
    const Ic = Icons[icon] || Icons.Circle;
    const active = currentQkey === qkey;
    const rawKey = countKey || qkey;
    const cnt = useUB ? (UB[rawKey] || 0) : (counts[rawKey] || 0);
    return (
      <button onClick={() => goTo(qkey)} key={qkey} data-testid={testid || `nav-cc-${qkey}`}
        className={cn("w-full flex items-center gap-2 rounded-lg py-1.5 text-[12px] transition-colors",
          indent ? "pl-7 pr-2" : "pl-2.5 pr-2",
          active ? "bg-[#2F80ED] text-white font-semibold" : "text-[#AEB9CC] hover:bg-[#283548] hover:text-white")}>
        <Ic className={cn("h-3.5 w-3.5 shrink-0", active ? "text-white" : tone || "text-[#AEB9CC]/70")} />
        <span className="flex-1 text-left truncate">{label}</span>
        {cnt > 0 && (
          <span className={cn("text-[10px] min-w-[18px] text-center px-1 py-0.5 rounded-full font-semibold",
            active ? "bg-white/20 text-white" : "bg-[#283548] text-[#AEB9CC]")}>
            {cnt > 99 ? "99+" : cnt}
          </span>
        )}
      </button>
    );
  };
  const renderNavItem = (qkey, label, icon, { countKey, tone, indent, testid, useUB } = {}) => NavItem({ qkey, label, icon, countKey, tone, indent, testid, useUB });

  const t = themeFor("communications");
  const headerBadge = badges?.communications;

  return (
    <div className={cn("rounded-xl p-2 transition-shadow", t.card)} data-testid="nav-group-communications">
      <div className="flex items-center gap-2">
        <NavLink to="/communications/center" onClick={onNavigate} data-testid="nav-communications"
          className="flex items-center gap-2.5 flex-1 min-w-0 rounded-lg px-1 py-1 hover:bg-[#283548] transition-colors">
          <span className={cn("h-9 w-9 rounded-lg flex items-center justify-center shadow-sm shrink-0", t.tile)}>
            <NavIcon name="MessagesSquare" className={cn("h-[18px] w-[18px]", t.tileIcon)} />
          </span>
          <span className={cn("truncate text-[13px] font-bold uppercase tracking-wide", t.label)}>Comms Center</span>
        </NavLink>
        <Badge value={headerBadge} testid="nav-badge-communications" />
        <button onClick={toggle} data-testid="nav-group-toggle-communications" aria-label="Toggle Communications submenu"
          className="shrink-0 p-1.5 rounded-md text-[#AEB9CC] hover:text-white hover:bg-[#283548] transition-colors">
          <Icons.ChevronDown className={cn("h-4 w-4 transition-transform duration-150", open ? "" : "-rotate-90")} />
        </button>
      </div>

      {open && (
        <div className="mt-1 space-y-0.5" data-testid="nav-cc-nav">
          {renderNavItem("all_comms", "All Communications", "MessagesSquare", { countKey: "all" })}

          {/* Channels section header */}
          <div className="px-2.5 pt-2 pb-0.5">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-[#4B5563]">Channels</span>
          </div>

          {/* INTERNAL (Staff & Management only) */}
          <div>
            <div className="flex items-center gap-0">
              <button onClick={() => setInternalOpen(v => !v)} data-testid="nav-cc-internal-toggle"
                className={cn("flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-l-lg text-[12px] transition-colors",
                  currentQkey?.startsWith("int_") ? "bg-[#2F80ED]/20 text-violet-300 font-medium" : "text-[#AEB9CC] hover:bg-[#283548] hover:text-white")}>
                <Icons.Lock className="h-3.5 w-3.5 shrink-0 text-violet-400" />
                <span className="flex-1 text-left">Internal</span>
                {(counts.ch_internal || 0) > 0 && (
                  <span className="text-[10px] px-1 py-0.5 rounded-full bg-[#283548] text-[#AEB9CC]">{counts.ch_internal}</span>
                )}
              </button>
              <button onClick={() => setInternalOpen(v => !v)} className="px-1.5 py-1.5 text-[#AEB9CC] hover:text-white hover:bg-[#283548] rounded-r-lg transition-colors">
                <Icons.ChevronDown className={cn("h-3.5 w-3.5 transition-transform", internalOpen ? "" : "-rotate-90")} />
              </button>
            </div>
            {internalOpen && (
              <div className="ml-5 mt-0.5 space-y-0.5 border-l border-[#283548] pl-2">
                <span className="block px-2 pt-1 pb-0.5 text-[8px] font-semibold uppercase tracking-widest text-violet-500/60">Staff Only</span>
                {renderNavItem("int_my_messages", "My Messages", "MessageCircle", { countKey: "int_my_messages", tone: "text-violet-400", indent: true })}
                {renderNavItem("int_teams", "Team Conversations", "Users", { countKey: "int_teams", tone: "text-violet-400", indent: true })}
                {renderNavItem("int_announcements", "Firm Announcements", "Megaphone", { countKey: "int_announcements", tone: "text-violet-400", indent: true })}
                <button onClick={() => { navigate("/communications/center?action=new_internal"); onNavigate?.(); }}
                  data-testid="nav-cc-new-internal"
                  className="w-full flex items-center gap-2 pl-7 pr-2 py-1.5 rounded-lg text-[12px] text-violet-400 hover:bg-[#283548] hover:text-violet-200 transition-colors">
                  <Icons.Plus className="h-3.5 w-3.5 shrink-0" />
                  <span className="flex-1 text-left">New Message</span>
                </button>
              </div>
            )}
          </div>

          {/* Primary external channels: Email, Secure Portal, Website Chat */}
          {PRIMARY_CH_DEFS.map(ch => {
            const Ic = Icons[ch.icon] || Icons.Circle;
            const anySubActive = ch.subs.some(([sk]) => currentQkey === sk);
            const isExp = channelExpanded[ch.key] !== undefined ? channelExpanded[ch.key] : anySubActive;
            const chActive = currentQkey === ch.key;
            const cnt = UB[ch.key] || 0;
            return (
              <div key={ch.key}>
                <div className="flex items-center">
                  <button onClick={() => goTo(ch.key)} data-testid={`nav-cc-${ch.key}`}
                    className={cn("flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-l-lg text-[12px] transition-colors",
                      chActive ? "bg-[#2F80ED] text-white font-semibold" : "text-[#AEB9CC] hover:bg-[#283548] hover:text-white")}>
                    <Ic className={cn("h-3.5 w-3.5 shrink-0", chActive ? "text-white" : ch.tone)} />
                    <span className="flex-1 text-left">{ch.label}</span>
                    {cnt > 0 && (
                      <span className={cn("text-[10px] min-w-[18px] text-center px-1 py-0.5 rounded-full font-semibold",
                        chActive ? "bg-white/20 text-white" : "bg-red-500/90 text-white")}>
                        {cnt > 99 ? "99+" : cnt}
                      </span>
                    )}
                  </button>
                  <button onClick={() => setChannelExpanded(p => ({ ...p, [ch.key]: !isExp }))}
                    data-testid={`nav-cc-expand-${ch.key}`}
                    className="px-1.5 py-1.5 text-[#AEB9CC] hover:text-white hover:bg-[#283548] rounded-r-lg transition-colors">
                    <Icons.ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isExp ? "" : "-rotate-90")} />
                  </button>
                </div>
                {isExp && (
                  <div className="ml-5 mt-0.5 space-y-0.5 border-l border-[#283548] pl-2">
                    {ch.subs.map(([sk, sl, si]) => {
                      const SubIc = Icons[si] || Icons.Circle;
                      const subActive = currentQkey === sk;
                      const sCnt = UB[sk] || 0;
                      return (
                        <button key={sk} onClick={() => goTo(sk)} data-testid={`nav-cc-${sk}`}
                          className={cn("w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] transition-colors",
                            subActive ? "bg-[#2F80ED] text-white font-semibold" : "text-[#AEB9CC] hover:bg-[#283548] hover:text-white")}>
                          <SubIc className={cn("h-3 w-3 shrink-0", subActive ? "text-white" : "text-[#AEB9CC]/70")} />
                          <span className="flex-1 text-left">{sl}</span>
                          {sCnt > 0 && <span className={cn("text-[10px] px-1 py-0.5 rounded-full font-semibold", subActive ? "bg-white/20 text-white" : "bg-red-500/90 text-white")}>{sCnt}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* More Channels — SMS, Phone Calls, WhatsApp Coming Soon */}
          <div>
            <button onClick={() => setMoreChannelsOpen(v => !v)} data-testid="nav-cc-more-channels-toggle"
              className="w-full px-2.5 py-1 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-widest text-[#4B5563] hover:text-[#AEB9CC] transition-colors mt-1">
              <Icons.ChevronRight className={cn("h-3 w-3 transition-transform", moreChannelsOpen ? "rotate-90" : "")} />
              <span>More Channels</span>
            </button>
            {moreChannelsOpen && (
              <div className="space-y-0.5">
                {MORE_CH_DEFS.map(ch => {
                  const Ic = Icons[ch.icon] || Icons.Circle;
                  const chActive = currentQkey === ch.key;
                  const cnt = UB[ch.key] || 0;
                  return (
                    <button key={ch.key} onClick={() => goTo(ch.key)} data-testid={`nav-cc-${ch.key}`}
                      className={cn("w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[12px] transition-colors",
                        chActive ? "bg-[#2F80ED] text-white font-semibold" : "text-[#AEB9CC] hover:bg-[#283548] hover:text-white")}>
                      <Ic className={cn("h-3.5 w-3.5 shrink-0", chActive ? "text-white" : ch.tone)} />
                      <span className="flex-1 text-left">{ch.label}</span>
                      {cnt > 0 && <span className={cn("text-[10px] min-w-[18px] text-center px-1 py-0.5 rounded-full font-semibold", chActive ? "bg-white/20 text-white" : "bg-red-500/90 text-white")}>{cnt > 99 ? "99+" : cnt}</span>}
                    </button>
                  );
                })}
                {/* WhatsApp — Coming Soon */}
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg opacity-50 cursor-not-allowed" data-testid="nav-cc-whatsapp-coming-soon">
                  <Icons.MessageCircle className="h-3.5 w-3.5 text-green-400 shrink-0" />
                  <span className="flex-1 text-left text-[12px] text-[#AEB9CC]">WhatsApp</span>
                  <span className="text-[8px] font-bold bg-green-900/40 text-green-400 px-1.5 py-0.5 rounded-full whitespace-nowrap">Coming Soon</span>
                </div>
              </div>
            )}
          </div>

          {/* More Filters — management only */}
          {isMgmt && (
            <div>
              <button onClick={() => setMoreFiltersOpen(v => !v)} data-testid="nav-cc-more-filters-toggle"
                className="w-full px-2.5 py-1 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-widest text-[#4B5563] hover:text-[#AEB9CC] transition-colors mt-1">
                <Icons.ChevronRight className={cn("h-3 w-3 transition-transform", moreFiltersOpen ? "rotate-90" : "")} />
                <span>More Filters</span>
              </button>
              {moreFiltersOpen && (
                <div className="space-y-0.5">
                {renderNavItem("inactive_clients", "Inactive Clients", "UserMinus", { countKey: "inactive_client_linked" })}
                {renderNavItem("identity_pending", "Identity Pending", "UserSearch", { countKey: "identity_pending_live_email", tone: "text-amber-400" })}
                </div>
              )}
            </div>
          )}

          {/* Closed */}
          <div>
            <button onClick={() => setClosedOpen(v => !v)} data-testid="nav-cc-closed-toggle"
              className="w-full px-2.5 py-1 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-widest text-[#4B5563] hover:text-[#AEB9CC] transition-colors mt-1">
              <Icons.ChevronRight className={cn("h-3 w-3 transition-transform", closedOpen ? "rotate-90" : "")} />
              <span>Closed</span>
            </button>
            {closedOpen && (
              <div className="space-y-0.5">
                {renderNavItem("resolved", "Resolved", "CheckCircle2", {})}
                {renderNavItem("archived", "Archived", "Archive", {})}
                {renderNavItem("trash", "Trash", "Trash2", {})}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NavList({ tree, collapsed, badges, onNavigate }) {
  return (
    <nav className={cn("flex-1 min-h-0 py-4", collapsed ? "px-2 space-y-2 overflow-visible" : "px-3 space-y-2.5 overflow-y-auto")} data-testid="sidebar-nav"
      style={collapsed ? {} : { scrollbarWidth: "thin", scrollbarColor: "#283548 transparent" }}>
      {tree.map(({ item, children }) => {
        if (item.key === "ops_center" && !collapsed) {
          return <OpsCenterDeptCard key={item.key} item={item} children={children} badges={badges} onNavigate={onNavigate} />;
        }
        if (item.key === "communications" && !collapsed) {
          return <CommsDeptCard key={item.key} item={item} badges={badges} onNavigate={onNavigate} />;
        }
        return collapsed
          ? <DeptRail key={item.key} item={item} children={children} badges={badges} onNavigate={onNavigate} />
          : <DeptCard key={item.key} item={item} children={children} badges={badges} onNavigate={onNavigate} />;
      })}
    </nav>
  );
}

function Brand({ collapsed }) {
  return (
    <div className={cn("h-16 flex items-center border-b border-[#283548] shrink-0", collapsed ? "justify-center px-2" : "px-4")}>
      {collapsed ? (
        <div className="h-9 w-9 rounded-lg bg-[#2F80ED] flex items-center justify-center shadow-sm">
          <Icons.Receipt className="h-5 w-5 text-white" strokeWidth={2.2} />
        </div>
      ) : (
        <img src="/taxflow-logo.png" alt="Tax Flow — Tax Accounting Network" className="h-9 w-auto object-contain brightness-0 invert" data-testid="sidebar-logo" />
      )}
    </div>
  );
}

function ProfileCard({ user, collapsed }) {
  const initials = (user?.name || "U").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  if (collapsed) {
    return (
      <div className="flex justify-center py-3 border-b border-[#283548] shrink-0" data-testid="sidebar-user">
        <div className="h-9 w-9 rounded-full bg-[#2F80ED] text-white flex items-center justify-center text-xs font-semibold ring-2 ring-[#283548] shadow-sm">{initials}</div>
      </div>
    );
  }
  return (
    <div className="px-4 py-3 border-b border-[#283548] shrink-0" data-testid="sidebar-user">
      <div className="flex items-center gap-3 rounded-xl bg-[#283548] border border-[#2F80ED]/20 px-3 py-2">
        <div className="relative">
          <div className="h-9 w-9 rounded-full bg-[#2F80ED] text-white flex items-center justify-center text-xs font-semibold shrink-0">{initials}</div>
          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-[#283548]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
          <div className="text-[11px] text-[#AEB9CC] truncate">{user?.role_label}</div>
        </div>
      </div>
    </div>
  );
}

function Footer({ collapsed, pinned, onToggleCollapse, onTogglePinned, showControls }) {
  if (!showControls) return null;
  return (
    <div className="border-t border-[#283548] shrink-0 p-2 space-y-1">
      <button
        onClick={onTogglePinned}
        data-testid="sidebar-pin-toggle"
        title={pinned ? "Unpin (auto-collapse, expand on hover)" : "Pin sidebar open"}
        className={cn("hidden md:flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
          pinned ? "text-[#2F80ED] bg-[#2F80ED]/15 hover:bg-[#2F80ED]/25" : "text-[#AEB9CC] hover:text-white hover:bg-[#283548]",
          collapsed && "justify-center")}
      >
        <Icons.Pin className={cn("h-4 w-4", pinned && "fill-current")} />
        {!collapsed && <span>{pinned ? "Pinned" : "Unpinned"}</span>}
      </button>
      <button
        onClick={onToggleCollapse}
        data-testid="sidebar-collapse-toggle"
        className={cn("hidden md:flex w-full items-center gap-2 px-3 py-2 rounded-lg text-[#AEB9CC] hover:text-white hover:bg-[#283548] transition-colors text-sm", collapsed && "justify-center")}
        title={collapsed ? "Expand" : "Collapse"}
      >
        {collapsed ? <Icons.PanelLeftOpen className="h-4 w-4" /> : (<><Icons.PanelLeftClose className="h-4 w-4" /><span>Collapse Menu</span></>)}
      </button>
    </div>
  );
}

export default function Sidebar({ collapsed, onToggleCollapse, badges, mobileOpen, onMobileClose }) {
  const { user } = useAuth();
  const { pinned, togglePinned } = useLayout();
  const [hover, setHover] = useState(false);

  // Unpinned: show the rail, expand on hover. Pinned: follow the manual collapse toggle.
  const effectiveCollapsed = pinned ? collapsed : !hover;

  const allowed = new Set(user?.modules || []);
  const permKeys = new Set(user?.permission_keys || []);
  const hasPerm = (p) => permKeys.has("*") || permKeys.has(p);
  const isAllowed = (i) => {
    if (i.hideWhenModule && allowed.has(i.hideWhenModule)) return false;
    if (i.roles && !i.roles.includes(user?.role)) return false;
    if (i.permission && !hasPerm(i.permission)) return false;
    if (i.modules) return i.modules.some((m) => allowed.has(m));
    // Role-only items (no module/modules requirement) are allowed once the role check above passes.
    // Without this guard they fall to allowed.has(i.key) which is false for keys not in MODULES.
    if (i.roles && !i.module && !i.modules) return true;
    return allowed.has(i.key) || (i.module && allowed.has(i.module));
  };
  const visible = NAV_ITEMS.filter(isAllowed);
  const visibleKeys = new Set(visible.map((i) => i.key));
  const tree = visible
    .filter((i) => !i.parent || !visibleKeys.has(i.parent))
    .map((i) => ({ item: i, children: i.children ? visible.filter((c) => c.parent === i.key) : [] }));

  return (
    <>
      {/* Desktop */}
      <aside
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={cn("bg-[#1C2431] border-r border-[#283548] flex-shrink-0 hidden md:flex md:flex-col transition-[width] duration-200", effectiveCollapsed ? "w-[76px]" : "w-[264px]")}
        data-testid="sidebar-desktop"
      >
        <Brand collapsed={effectiveCollapsed} />
        <ProfileCard user={user} collapsed={effectiveCollapsed} />
        <NavList tree={tree} collapsed={effectiveCollapsed} badges={badges} />
        <Footer collapsed={effectiveCollapsed} pinned={pinned} onToggleCollapse={onToggleCollapse} onTogglePinned={togglePinned} showControls />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="sidebar-mobile">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onMobileClose} />
          <aside className="absolute left-0 top-0 h-full w-[264px] bg-[#1C2431] border-r border-[#283548] flex flex-col animate-in slide-in-from-left duration-200">
            <Brand collapsed={false} />
            <ProfileCard user={user} collapsed={false} />
            <NavList tree={tree} collapsed={false} badges={badges} onNavigate={onMobileClose} />
          </aside>
        </div>
      )}
    </>
  );
}
